import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useFilteredNotifications } from "@/hooks/useFilteredNotifications";
import { PatchTask } from "@/services/fhir/task/PatchTask";
import { currentFeatures, oids } from "@/configs/Settings";
import {
  getPatientIdForSummaryFromTask,
  isQuestionnaireAnsweredNotificationTask,
} from "@/utils/taskNotificationUtils";
import {
  Close,
  Description,
  MarkEmailReadOutlined,
  MarkEmailUnreadOutlined,
  NotificationsOutlined,
  OpenInNew,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Chip,
  ClickAwayListener,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { Task } from "fhir/r5";
import {
  getAppointmentReferenceFromQuestionnaireTask,
  isQuestionnaireTask,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";

interface NotificationListProps {
  loading: boolean;
  /** Quando "patient", tasks de questionário só exibem ação "Responder" (sem marcar como lida). */
  accessType?: string | null;
  tasks: Task[];
  updatingById: Record<string, boolean>;
  handleToggleReadStatus: (task: Task) => Promise<void>;
  handleAccessMedicalRecord: (task: Task) => Promise<void>;
  handleRespondQuestionnaire: (task: Task) => void;
  formatDateTimePtBr: (dateIsoString?: string) => string;
}

const NotificationList: React.FC<NotificationListProps> = ({
  loading,
  accessType,
  tasks,
  updatingById,
  handleToggleReadStatus,
  handleAccessMedicalRecord,
  handleRespondQuestionnaire,
  formatDateTimePtBr,
}) => {
  if (loading) {
    return (
      <List sx={{ p: 0 }}>
        {[1, 2].map((_, index) => (
          <ListItem
            key={`skeleton-${index}`}
            sx={{ borderBottom: "1px solid #eee" }}
          >
            <ListItemText
              primary={<Skeleton variant="text" width="80%" />}
              secondary={
                <>
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton
                    variant="rectangular"
                    width={80}
                    height={24}
                    sx={{ mt: 1, borderRadius: 1 }}
                  />
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma notificação encontrada
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {tasks.map((task, index) => {
        const isPatientView = accessType === "patient";
        const isQ = isQuestionnaireTask(task);
        const showPatientQuestionnaireOnly =
          isPatientView && isQ && task.status === "requested";

        return (
          <ListItem
            key={task.id ?? `task-${index}`}
            sx={{
              borderBottom: "1px solid #eee",
              "&:hover": { bgcolor: "rgba(0, 168, 132, 0.08)" },
            }}
          >
            <ListItemText
              primary={task.description || "Tarefa sem descrição"}
              secondary={
                <React.Fragment>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    {typeof task.code === "object" && task.code.text
                      ? task.code.text
                      : "Sem detalhes adicionais"}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={
                        isPatientView && isQ
                          ? task.status === "requested"
                            ? "Pendente"
                            : task.status === "completed"
                              ? "Respondido"
                              : task.status || "pendente"
                          : task.status === "requested"
                            ? "Não lido"
                            : task.status === "completed"
                              ? "Lido"
                              : task.status || "pendente"
                      }
                      color={
                        task.status === "completed"
                          ? "success"
                          : task.status === "in-progress"
                            ? "primary"
                            : "warning"
                      }
                      size="small"
                    />
                    {task?.authoredOn && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTimePtBr(task.authoredOn)}
                      </Typography>
                    )}
                  </Stack>
                </React.Fragment>
              }
            />
            {updatingById[task.id ?? ""] ? (
              <Skeleton variant="circular" width={60} height={32} />
            ) : showPatientQuestionnaireOnly ? (
              <Tooltip title="Responder questionário" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRespondQuestionnaire(task);
                  }}
                >
                  <OpenInNew color="primary" />
                </IconButton>
              </Tooltip>
            ) : isPatientView && isQ ? (
              // Questionário já respondido: sem ação manual de "lido" — só o fluxo de resposta conclui a task.
              null
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {isQuestionnaireAnsweredNotificationTask(task) ? (
                  <Tooltip title="Acessar prontuário" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleAccessMedicalRecord(task);
                      }}
                    >
                      <Description />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip
                  title={
                    task.status === "completed"
                      ? "Marcar como não lido"
                      : "Marcar como lido"
                  }
                  arrow
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleToggleReadStatus(task);
                    }}
                  >
                    {task.status === "requested" ? (
                      <MarkEmailUnreadOutlined color="warning" />
                    ) : (
                      <MarkEmailReadOutlined color="success" />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

const HeaderNotification: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const theme = useTheme();
  const { user, access } = useSessionContext();
  const { setIsSidebarExpanded } = useSidebarNavigation();
  const { allTasks, updateTaskInContext } = useTaskWatchingContext();
  const navigate = useNavigate();
  const { filteredTasks, unreadCount } = useFilteredNotifications({
    allTasks,
    user,
    access,
  });

  const myPatientReference = useMemo(() => {
    const username = user?.username;
    if (typeof username !== "string" || !/^\d{11}$/.test(username)) {
      return "";
    }
    return `Patient/${oids.cpf}-${username}`;
  }, [user?.username]);

  const [updatingById, setUpdatingById] = useState<Record<string, boolean>>({});
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  /**
   * Marca uma tarefa como "lida" atualizando o status para "completed" via PATCH.
   * O hook useFilteredNotifications reflete a mudança via updateTaskInContext.
   */
  const handleToggleReadStatus = async (task: Task) => {
    const taskId = task.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));
      const isCompleted = task?.status === "completed";
      const nextStatus = isCompleted ? "requested" : "completed";

      await PatchTask(taskId, [
        { op: "replace", path: "/status", value: nextStatus },
      ]);
      updateTaskInContext(taskId, { status: nextStatus });
    } catch (error) {
      console.error("Erro ao marcar tarefa como lida:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  /**
   * Ação específica para notificação de resposta de questionário:
   * - navega para o prontuário ("/summary") com o mesmo padrão do Patient Search (state.patientId)
   * - conclui a task (status=completed) e reflete no contexto imediatamente
   */
  const handleAccessMedicalRecord = async (task: Task) => {
    const taskId = task?.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));

      const patientId = getPatientIdForSummaryFromTask(task, {
        isDefaultApp: Boolean(currentFeatures.defaultApp),
      });
      if (!patientId) {
        console.warn(
          "Não foi possível derivar patientId para /summary a partir da task",
          task,
        );
        return;
      }

      if (task?.status !== "completed") {
        await PatchTask(taskId, [
          { op: "replace", path: "/status", value: "completed" },
        ]);
        updateTaskInContext(taskId, { status: "completed" });
      }

      setNotificationAnchorEl(null);
      navigate("/summary", { state: { patientId } });
    } catch (error) {
      console.error("Erro ao acessar prontuário via notificação:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  /**
   * Paciente: mesma navegação que PatientQuestionnaireTasksSection — sem PATCH de "lido" aqui;
   * a task só conclui ao enviar a resposta em /questionnaire-respond.
   */
  const handleRespondQuestionnaire = useCallback(
    (task: Task) => {
      const focusReference = task.focus?.reference?.trim() ?? "";
      const questionnaireId = focusReference.startsWith("Questionnaire/")
        ? focusReference.replace("Questionnaire/", "").trim()
        : "";

      const patientTaskId = task.id?.trim() ?? "";
      const subjectReference = myPatientReference.trim();
      const requesterPractitionerRoleReference =
        task.requester?.reference?.trim() ?? "";
      const ownerOrganizationReference = task.owner?.reference?.trim() ?? "";
      const questionnaireTitle = (task.description || "Questionário").trim();
      const appointmentReference =
        getAppointmentReferenceFromQuestionnaireTask(task);

      if (
        !questionnaireId ||
        !patientTaskId ||
        !subjectReference ||
        !requesterPractitionerRoleReference ||
        !ownerOrganizationReference
      ) {
        toast.error(
          "Não foi possível abrir o questionário (dados da task incompletos).",
        );
        return;
      }

      setIsSidebarExpanded(false);
      setNotificationAnchorEl(null);
      navigate("/questionnaire-respond", {
        state: {
          questionnaireId,
          questionnaireTitle,
          subjectReference,
          patientTaskId,
          requesterPractitionerRoleReference,
          ownerOrganizationReference,
          ...(appointmentReference ? { appointmentReference } : {}),
        },
      });
    },
    [myPatientReference, navigate, setIsSidebarExpanded],
  );

  const formatDateTimePtBr = (dateIsoString?: string): string => {
    if (!dateIsoString) return "";
    const parsed = dayjs(dateIsoString);
    if (!parsed.isValid()) return "";
    return parsed.format("DD/MM/YYYY HH:mm");
  };
  return (
    <>
      <IconButton
        color="inherit"
        onClick={(event) =>
          setNotificationAnchorEl(
            notificationAnchorEl ? null : event.currentTarget,
          )
        }
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount || 0} color="error">
          <NotificationsOutlined />
        </Badge>
      </IconButton>

      {/* Popper de Notificações */}

      {isMobile ? (
        <Dialog
          open={Boolean(notificationAnchorEl)}
          onClose={() => setNotificationAnchorEl(null)}
          fullScreen
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <Paper sx={{ width: "100%", maxHeight: "100%", overflow: "auto" }}>
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Notificações
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "neutral500.main" }}
                >
                  (últimos 30 dias)
                </Typography>
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <IconButton onClick={() => setNotificationAnchorEl(null)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>

            <NotificationList
              loading={false}
              accessType={access?.type}
              tasks={filteredTasks}
              updatingById={updatingById}
              handleToggleReadStatus={handleToggleReadStatus}
              handleAccessMedicalRecord={handleAccessMedicalRecord}
              handleRespondQuestionnaire={handleRespondQuestionnaire}
              formatDateTimePtBr={formatDateTimePtBr}
            />
          </Paper>
        </Dialog>
      ) : (
        <Popper
          open={Boolean(notificationAnchorEl)}
          anchorEl={notificationAnchorEl}
          placement="bottom-end"
          style={{ zIndex: theme.zIndex.drawer + 2 }}
        >
          <ClickAwayListener onClickAway={() => setNotificationAnchorEl(null)}>
            <Paper sx={{ width: 360, maxHeight: 480, overflow: "auto" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Notificações
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "neutral500.main" }}
                >
                  (últimos 30 dias)
                </Typography>
              </Typography>

              <NotificationList
                loading={false}
                accessType={access?.type}
                tasks={filteredTasks}
                updatingById={updatingById}
                handleToggleReadStatus={handleToggleReadStatus}
                handleAccessMedicalRecord={handleAccessMedicalRecord}
                handleRespondQuestionnaire={handleRespondQuestionnaire}
                formatDateTimePtBr={formatDateTimePtBr}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export { HeaderNotification };
